import Alert from "@mui/material/Alert";
import * as React from "react";
import { Link } from "react-router";

import {
  AUTO_REFRESH_INTERVAL_MS,
  InvocationActivityPanel,
  type TimeRange,
} from "@/shared/components/InvocationActivity";
import { TriggerLink } from "@/shared/components/links";
import {
  ResourceView,
  composeFilters,
  useResourceView,
  type Filter,
  type FilterDefinition,
  type FilterMatchMode,
} from "@/shared/components/ResourceView";
import { UserLink } from "@/shared/components/UserLink";
import { FormattedDateTime } from "@/shared/datetime";
import {
  type ActionRecord,
  type InvocationRecord,
  InvocationSource,
  InvocationStatus,
  isDataSourceSpecified,
} from "@/shared/domain/actions";
import { useActions, useInvocationSearch } from "@/shared/domain/actions/hooks";
import {
  invocationActionCondition,
  invocationDatasetCondition,
  invocationInitiatorCondition,
} from "@/shared/domain/actions/invocationSearchConditions";
import {
  OrganizationStatus,
  OrganizationTier,
  OrganizationType,
  type OrgRecord,
} from "@/shared/domain/orgs";
import {
  ColumnVisibility,
  DataTable,
  cellStyles,
  colId,
  createColumnHelper,
  type ColumnPickerOption,
  deriveLoadingState,
  EmptyCell,
  type ICellRendererParams,
  type ValueGetterParams,
} from "@/shared/foundation/primitives/Table";
import type {
  Condition,
  ConditionsOperator,
  SearchQueryBody,
  SortDirection,
} from "@/types";

import { ActionLink } from "./action/ActionLink";
import InvokeActionButton from "./action/InvokeActionButton";
import CancelInvocationIconButton from "./invocation/CancelInvocationIconButton";
import CloneInvocationIconButton from "./invocation/CloneInvocationIconButton";
import { createMechanicalTimeStr } from "./invocation/InvocationRuntimeText/helpers";
import InvocationStatusChip from "./invocation/InvocationStatusChip";
import { CancelAll } from "./InvocationControlBar/CancelAll";
import styles from "./Invocations.module.css";

interface InvocationsProps {
  currentOrg: OrgRecord | null;
  datasetId?: string;
  action?: ActionRecord;
  triggerId?: string;
  condition?: Condition;
}

const AUTO_REFRESH_STORAGE_KEY = "invocations.autoRefresh";
const LIVE_ROWS_POLL_MS = 10_000;
/** With no bound on "created", the activity panel shows this much history. */
const OPEN_WINDOW_MS = 365 * 24 * 60 * 60 * 1000;
const CREATED = "created";
const STATUS = "last_status";

const STATUS_OPTIONS = [
  { label: "Pending", value: String(InvocationStatus.Queued) },
  { label: "Provisioning", value: String(InvocationStatus.Scheduled) },
  { label: "Downloading", value: String(InvocationStatus.Downloading) },
  { label: "Processing", value: String(InvocationStatus.Processing) },
  { label: "Uploading", value: String(InvocationStatus.Uploading) },
  { label: "Completed", value: String(InvocationStatus.Completed) },
  { label: "Cancelled", value: String(InvocationStatus.Cancelled) },
  { label: "Failed", value: String(InvocationStatus.Failed) },
  { label: "Deadly", value: String(InvocationStatus.Deadly) },
];

/** Every status the picker offers, for resolving a NOT_EQUALS selection to its complement. */
const FILTERABLE_STATUSES: InvocationStatus[] = STATUS_OPTIONS.map((option) =>
  Number(option.value),
);

const kindaSketchyCurrentOrgFromJustId = (orgId: string): OrgRecord => ({
  org_id: orgId,
  org_type: OrganizationType.Team,
  created: "2024-01-01T00:00Z",
  name: "FAKE_ORG",
  tier: OrganizationTier.Free,
  status: OrganizationStatus.Active,
});

const columnHelper = createColumnHelper<InvocationRecord>();

function lastStatus(record: InvocationRecord): InvocationStatus {
  return record.status[record.status.length - 1]?.status ?? record.last_status;
}

interface CellDeps {
  org: OrgRecord | null;
  refetch: () => void;
}

/**
 * Column ids double as sort fields, so only the columns the backend can sort on are sortable:
 * created, duration and last_status. Cell renderers carry their own links; the grid has no
 * row-click. `deps` is a ref so the column array stays stable across renders. The status cell
 * reads `refetch` when its cancel button fires, so it always calls the current one; it reads
 * `org` while rendering, so a cell painted before `currentOrg` arrives keeps the stand-in org
 * until AG Grid re-renders that cell.
 */
function buildColumns(deps: React.MutableRefObject<CellDeps>) {
  return [
    columnHelper.accessor("invocation_id", {
      headerName: "Invocation",
      width: 200,
      cellClass: cellStyles.monoCell,
      hide: false,
      cellRenderer: (p: ICellRendererParams<InvocationRecord, string>) =>
        p.data ? (
          <Link
            to={`/invocations/${p.data.invocation_id}`}
            className={styles.link}
          >
            {p.data.invocation_id}
          </Link>
        ) : null,
    }),
    {
      colId: STATUS,
      headerName: "Status",
      width: 200,
      sortable: true,
      hide: false,
      valueGetter: (p: ValueGetterParams<InvocationRecord>) =>
        p.data ? lastStatus(p.data) : null,
      cellRenderer: (
        p: ICellRendererParams<InvocationRecord, InvocationStatus>,
      ) => {
        const row = p.data;
        if (!row) {
          return null;
        }
        const org =
          deps.current.org ?? kindaSketchyCurrentOrgFromJustId(row.org_id);
        return (
          <span className={styles.statusCell}>
            <InvocationStatusChip
              size="small"
              status={lastStatus(row)}
              variant="outlined"
            />
            <CloneInvocationIconButton invocation={row} org={org} />
            <CancelInvocationIconButton
              invocation={row}
              onCancel={deps.current.refetch}
              org={org}
            />
          </span>
        );
      },
    },
    {
      colId: "action",
      headerName: "Action",
      flex: 1,
      minWidth: 160,
      hide: false,
      // An invocation is recorded against the org that ran it, so the row already names the
      // org this link is drawn for; taking it from there rather than from the session means a
      // row painted before the session resolves still points an Action Hub action at its
      // publishing org instead of at this one, where the name does not exist.
      cellRenderer: (p: ICellRendererParams<InvocationRecord>) =>
        p.data ? (
          <ActionLink
            action={p.data.provenance.action}
            currentOrgId={deps.current.org?.org_id ?? p.data.org_id}
          />
        ) : null,
    },
    {
      colId: "dataset",
      headerName: "Dataset",
      width: 200,
      cellClass: cellStyles.monoCell,
      hide: false,
      cellRenderer: (p: ICellRendererParams<InvocationRecord>) => {
        const row = p.data;
        if (!row) {
          return null;
        }
        return isDataSourceSpecified(row.data_source) ? (
          <Link
            to={`/datasets/${row.data_source.data_source_id}`}
            className={styles.link}
          >
            {row.data_source.data_source_id}
          </Link>
        ) : (
          <EmptyCell />
        );
      },
    },
    {
      colId: "duration",
      headerName: "Duration",
      width: 120,
      sortable: true,
      hide: false,
      valueGetter: (p: ValueGetterParams<InvocationRecord>) =>
        p.data ? createMechanicalTimeStr(p.data.duration) : "",
    },
    columnHelper.accessor("created", {
      headerName: "Created",
      width: 200,
      sortable: true,
      hide: false,
      cellRenderer: (p: ICellRendererParams<InvocationRecord, string>) =>
        p.value ? <FormattedDateTime value={p.value} /> : null,
    }),
    {
      colId: "submitter",
      headerName: "Run by",
      width: 200,
      hide: false,
      cellRenderer: (p: ICellRendererParams<InvocationRecord>) => {
        const source = p.data?.provenance.source;
        if (!source) {
          return null;
        }
        if (source.source_type === InvocationSource.Trigger) {
          return <TriggerLink triggerId={source.source_id} />;
        }
        if (source.source_type === InvocationSource.Manual) {
          return <UserLink userId={source.source_id} />;
        }
        return <span>{source.source_id}</span>;
      },
    },
  ];
}

/**
 * The page's default range: a rolling 24 hours rather than the calendar day. A preset resolves
 * against "now" on every query, so it needs no sliding of its own. `TODAY` anchors to UTC
 * midnight, which for a reader in the Americas shows only the minutes since that boundary for
 * most of their working afternoon.
 */
const DEFAULT_CREATED_FILTER: Filter = {
  type: "date",
  field: CREATED,
  comparator: "LAST_24_HOURS",
  values: [],
};

/**
 * The wall clock rounded down to the minute — the same anchor a date preset expands against.
 *
 * The panel's window and the table's `created` condition are both derived at render from this, so
 * the two describe the same span however long the page sits open. Reading the raw instant instead
 * would move the bounds on every render, and each move mints a query key and another fetch.
 */
function currentMinute(): number {
  return Math.floor(Date.now() / 60_000) * 60_000;
}

/**
 * The span a "created" filter describes, for the activity panel: both bounds from BETWEEN, the
 * missing one from `now` (or a year back) otherwise; a year back to now with no filter at all.
 */
function windowFromCreatedFilter(
  filter: Filter | undefined,
  now: number,
): TimeRange {
  const bounds = filter
    ? createdBounds(composeFilters([], [filter], "AND"))
    : {};
  // A preset's upper bound is the end of its day; the panel has nothing to say about the future.
  const end = new Date(Math.min(bounds.end?.getTime() ?? now, now));
  const start = bounds.start ?? new Date(end.getTime() - OPEN_WINDOW_MS);
  return { start, end };
}

function createdBounds(condition: Condition | undefined): {
  start?: Date;
  end?: Date;
} {
  if (!condition) {
    return {};
  }
  if ("conditions" in condition) {
    return condition.conditions.reduce<{ start?: Date; end?: Date }>(
      (acc, inner) => ({ ...acc, ...createdBounds(inner) }),
      {},
    );
  }
  if (condition.field !== CREATED || typeof condition.value !== "string") {
    return {};
  }
  const at = new Date(condition.value);
  switch (condition.comparator) {
    case "GREATER_THAN":
    case "GREATER_THAN_OR_EQUAL":
      return { start: at };
    case "LESS_THAN":
    case "LESS_THAN_OR_EQUAL":
      return { end: at };
    default:
      return {};
  }
}

/**
 * The status filter's selected statuses.
 *
 * It is an `enum` filter, so `values` holds `{value, label}` options whose `value` is a status
 * number as a string (see `STATUS_OPTIONS`); the display label never crosses this boundary. This
 * mirrors the projection `filterRowToCondition` performs for option-backed filters — the shared
 * path this one bypasses in order to emit numbers.
 */
function selectedStatuses(filter: Filter | undefined): InvocationStatus[] {
  if (!filter || filter.type !== "enum") {
    return [];
  }
  return filter.values
    .map((option) => Number(option.value))
    .filter((value): value is InvocationStatus => value in InvocationStatus);
}

/**
 * The table's status filter as internal statuses, for the panel to pick series by.
 *
 * A NOT_EQUALS filter keeps every status except the selected ones, so the panel draws the
 * complement. Returning the selected statuses instead would draw exactly the series the rows
 * beneath the chart exclude.
 */
function statusesOf(filter: Filter | undefined): InvocationStatus[] {
  const selected = selectedStatuses(filter);
  if (selected.length === 0) {
    return [];
  }
  if (filter?.comparator === "NOT_EQUALS") {
    return FILTERABLE_STATUSES.filter((status) => !selected.includes(status));
  }
  return selected;
}

/**
 * The status filter as a condition. The query API compares `last_status` against an integer
 * column, so the values are emitted as numbers rather than the strings `composeFilters` would
 * produce.
 */
function statusCondition(filter: Filter | undefined): Condition | undefined {
  const statuses = selectedStatuses(filter);
  if (statuses.length === 0) {
    return undefined;
  }
  const negative = filter?.comparator === "NOT_EQUALS";
  const conditions: Condition[] = statuses.map((status) => ({
    field: STATUS,
    comparator: negative ? "NOT_EQUALS" : "EQUALS",
    value: status,
  }));
  return conditions.length === 1
    ? conditions[0]
    : { operator: negative ? "AND" : "OR", conditions };
}

/** The conditions that are present, joined under `operator`; one alone is returned ungrouped. */
function join(
  operator: ConditionsOperator,
  conditions: (Condition | undefined)[],
): Condition | undefined {
  const present = conditions.filter((c): c is Condition => c !== undefined);
  if (present.length === 0) {
    return undefined;
  }
  return present.length === 1 ? present[0] : { operator, conditions: present };
}

/**
 * The invocations list: the activity panel above the grid, both driven by the filter bar above them.
 *
 * Status is applied to what the panel draws rather than what it fetches, and "created" gives the
 * panel its window rather than a condition; everything else the user picks goes to both. The
 * scope from props (a dataset, an action, a trigger, or a raw condition) is AND-ed under whatever
 * the user chooses, so their match mode cannot dissolve it.
 */
export const Invocations: React.FC<InvocationsProps> = ({
  currentOrg,
  datasetId,
  action,
  triggerId,
  condition,
}) => {
  // Auto-refresh and "Reset view" re-resolve the rolling window by forcing a render; the bounds
  // themselves come from the wall clock at render, never from a timestamp held in state. Holding
  // one would freeze the panel's window at the moment it was captured while the table's `created`
  // condition kept rolling, and the two would describe different spans the longer the page sat open.
  const [, reresolveWindow] = React.useReducer((count: number) => count + 1, 0);
  const [autoRefresh, setAutoRefresh] = React.useState<boolean>(() =>
    readStoredFlag(AUTO_REFRESH_STORAGE_KEY),
  );

  // Ingestion actions usually live in the Action Hub (roboto-public, or roboto-team on a private
  // stack) rather than in the org, so the picker offers both; a name filters by name alone.
  const orgActions = useActions("org");
  const hubActions = useActions("actionhub");
  const filterDefinitions = React.useMemo<FilterDefinition[]>(
    () => [
      {
        type: "enum",
        field: "provenance.action.name",
        name: "Action",
        options: Array.from(
          new Set(
            [...(orgActions.data ?? []), ...(hubActions.data ?? [])].map(
              (record) => record.name,
            ),
          ),
        )
          .sort()
          .map((name) => ({ label: name, value: name })),
      },
      { type: "enum", field: STATUS, name: "Status", options: STATUS_OPTIONS },
      {
        type: "enum",
        field: "provenance.source.source_type",
        name: "Source",
        options: [
          { label: "User", value: InvocationSource.Manual },
          { label: "Trigger", value: InvocationSource.Trigger },
        ],
      },
      {
        type: "string",
        field: "provenance.source.source_id",
        name: "Submitter",
        allowedComparators: ["EQUALS", "NOT_EQUALS"],
      },
      { type: "date", field: CREATED, name: "Created" },
      ...(datasetId === undefined
        ? [
            {
              type: "string" as const,
              field: "data_source_id",
              name: "Dataset",
              allowedComparators: ["EQUALS" as const, "NOT_EQUALS" as const],
            },
          ]
        : []),
    ],
    [orgActions.data, hubActions.data, datasetId],
  );

  const [hiddenAtMount] = React.useState(
    () => new Set(datasetId !== undefined ? ["dataset"] : []),
  );
  const cellDeps = React.useRef<CellDeps>({
    org: currentOrg,
    refetch: () => {},
  });
  cellDeps.current.org = currentOrg;
  const columns = React.useMemo(
    () =>
      buildColumns(cellDeps).map((column) =>
        hiddenAtMount.has(colId(column)) ? { ...column, hide: true } : column,
      ),
    [hiddenAtMount],
  );
  const columnOptions = React.useMemo<ColumnPickerOption[]>(
    () =>
      columns.map((c) => ({ id: colId(c), label: c.headerName ?? colId(c) })),
    [columns],
  );

  const view = useResourceView<InvocationRecord>({
    getRowId: (row) => row.invocation_id,
    initialSort: { columnId: CREATED, direction: "desc" },
    initialActiveFilters: {
      type: "structured",
      filters: [DEFAULT_CREATED_FILTER],
      matchMode: "AND",
    },
    defaultVisibleColumnIds: columns
      .filter((column) => column.hide !== true)
      .map(colId),
  });
  // `view` itself is a bare object literal with no stable identity; each field inside has one.
  const { applyFilters, commit, updateFilter } = view;
  const { resetSort } = view.table;

  const scopeCondition = React.useMemo<Condition | undefined>(() => {
    if (condition) {
      return condition;
    }
    if (datasetId) {
      return invocationDatasetCondition(datasetId);
    }
    if (action) {
      return invocationActionCondition(
        action.org_id ?? currentOrg?.org_id,
        action.name,
      );
    }
    if (triggerId) {
      return invocationInitiatorCondition(triggerId, "Trigger");
    }
    return undefined;
  }, [condition, datasetId, action, triggerId, currentOrg?.org_id]);

  const userFilters = React.useMemo<Filter[]>(
    () => (view.filters.type === "structured" ? view.filters.filters : []),
    [view.filters],
  );
  const matchMode: FilterMatchMode =
    view.filters.type === "structured" ? view.filters.matchMode : "AND";
  const statusFilter = userFilters.find((filter) => filter.field === STATUS);
  const createdFilter = userFilters.find((filter) => filter.field === CREATED);
  const otherFilters = userFilters.filter(
    (filter) => filter !== statusFilter && filter !== createdFilter,
  );

  // Status is built by hand rather than by `composeFilters`, so it has to be joined to the rest of
  // the user's chips under their own match mode: the picker above the grid says "match any" while
  // the chips include a status, and an AND here would ask the server for both.
  const userCondition = join(matchMode, [
    composeFilters(
      [],
      createdFilter ? [...otherFilters, createdFilter] : otherFilters,
      matchMode,
    ),
    statusCondition(statusFilter),
  ]);
  const tableCondition = join("AND", [scopeCondition, userCondition]);
  const panelCondition = join("AND", [
    scopeCondition,
    composeFilters([], otherFilters, matchMode),
  ]);
  // Read at render, so a render that advances the table's condition advances this with it; rounded
  // to the minute, so the renders in between leave both alone.
  const minute = currentMinute();
  const activityWindow = React.useMemo(
    () => windowFromCreatedFilter(createdFilter, minute),
    [createdFilter, minute],
  );
  const panelStatuses = React.useMemo(
    () => statusesOf(statusFilter),
    [statusFilter],
  );

  // Token chain for forward paging: the entry at N is the `after` for page N, reset whenever the
  // search shape changes (which also resets the page to 0).
  const [tokenChain, setTokenChain] = React.useState<Map<number, string>>(
    new Map(),
  );
  React.useEffect(() => {
    setTokenChain(new Map());
  }, [view.filters, view.table.sort, view.table.pageSize]);

  const after =
    view.table.currentPage > 0
      ? tokenChain.get(view.table.currentPage)
      : undefined;
  const query: SearchQueryBody = {
    ...(view.table.sort && {
      sort_by: view.table.sort.columnId,
      sort_direction: view.table.sort.direction.toUpperCase() as SortDirection,
    }),
    ...(tableCondition && { condition: tableCondition }),
    ...(after && { after }),
    limit: view.table.pageSize,
  };
  // One poll for the page while any row is still in flight, so every status chip draws from the
  // same fetch; the chips' own 2s timers would refetch the page once per running row.
  const [liveRows, setLiveRows] = React.useState(false);
  const search = useInvocationSearch({
    query,
    refetchInterval: pollInterval(autoRefresh, liveRows),
  });
  cellDeps.current.refetch = () => void search.refetch();
  const items = search.data?.items;
  React.useEffect(() => {
    setLiveRows(
      (items ?? []).some(
        (item) => lastStatus(item) < InvocationStatus.Completed,
      ),
    );
  }, [items]);

  const nextToken = search.data?.next_token ?? null;
  const currentPage = view.table.currentPage;
  React.useEffect(() => {
    if (nextToken === null) {
      return;
    }
    setTokenChain((previous) => {
      if (previous.get(currentPage + 1) === nextToken) {
        return previous;
      }
      const next = new Map(previous);
      next.set(currentPage + 1, nextToken);
      return next;
    });
  }, [nextToken, currentPage]);

  const setCreated = React.useCallback(
    (filter: Filter) => {
      const index = userFilters.findIndex((f) => f.field === CREATED);
      if (index >= 0) {
        updateFilter(index, filter);
      } else {
        commit({ type: "structured", filter });
      }
    },
    [userFilters, updateFilter, commit],
  );

  React.useEffect(() => {
    if (!autoRefresh) {
      return;
    }
    const interval = window.setInterval(
      reresolveWindow,
      AUTO_REFRESH_INTERVAL_MS,
    );
    return () => window.clearInterval(interval);
  }, [autoRefresh, reresolveWindow]);

  const handleWindowChange = React.useCallback(
    (range: TimeRange) => {
      setCreated({
        type: "date",
        field: CREATED,
        comparator: "BETWEEN",
        values: [range.start.toISOString(), range.end.toISOString()],
      });
    },
    [setCreated],
  );

  const handleReset = React.useCallback(() => {
    applyFilters({
      type: "structured",
      filters: [DEFAULT_CREATED_FILTER],
      matchMode: "AND",
    });
    resetSort();
    reresolveWindow();
  }, [applyFilters, resetSort, reresolveWindow]);

  const handleAutoRefreshChange = React.useCallback((on: boolean) => {
    setAutoRefresh(on);
    writeStoredFlag(AUTO_REFRESH_STORAGE_KEY, on);
  }, []);

  const isOrgWideList =
    currentOrg !== null &&
    datasetId === undefined &&
    triggerId === undefined &&
    action === undefined &&
    !condition;

  // Skeletons only when there is nothing current to show: the first load, or a page whose rows are
  // still the previous page's. A background refresh keeps the rows on screen.
  const loading = deriveLoadingState({
    isLoading: search.isLoading,
    isFetching: search.isFetching && search.isPlaceholderData,
  });

  return (
    <div className={styles.page}>
      {!datasetId && !condition && (
        <Alert severity="info">
          Invocations are runs of an action on files in a dataset.{" "}
          {currentOrg?.tier === OrganizationTier.Free && (
            <>
              In Roboto&apos;s free tier, invocations are run serially. Upgrade
              to{" "}
              <a
                href="https://www.roboto.ai/pricing"
                target="_blank"
                rel="noreferrer"
              >
                premium
              </a>{" "}
              to run them faster in parallel.
            </>
          )}
        </Alert>
      )}

      {/* The filter bar leads: it drives the panel below it as much as the grid. */}
      <ResourceView.Actions>
        <ResourceView.Filters
          filterDefinitions={filterDefinitions}
          onFilterSelected={commit}
        />
        <ColumnVisibility
          options={columnOptions}
          visibleColumnIds={view.table.visibleColumnIds}
          setColumnVisibility={view.table.setColumnVisibility}
        />
        <div className={styles.trailingActions}>
          <InvokeActionButton action={action} datasetId={datasetId} />
          <CancelAll datasetId={datasetId} />
        </div>
      </ResourceView.Actions>
      <ResourceView.ActiveFilters
        filters={view.filters}
        filterDefinitions={filterDefinitions}
        onRemove={view.removeFilter}
        onUpdate={updateFilter}
        onClear={view.clearFilters}
        onMatchModeChange={view.setMatchMode}
        onFilterSelected={commit}
      />

      <InvocationActivityPanel
        condition={panelCondition}
        statusFilter={panelStatuses}
        window={activityWindow}
        onWindowChange={handleWindowChange}
        defaultExpanded={isOrgWideList}
        storageKey={`invocations.activity.${activityContext({ datasetId, action, triggerId, condition })}`}
        autoRefresh={autoRefresh}
        onAutoRefreshChange={handleAutoRefreshChange}
        onReset={handleReset}
      />

      {search.isError && (
        <Alert severity="error">
          Failed to load invocations. {search.error?.message ?? ""} Please
          contact <a href="mailto:support@roboto.ai">support@roboto.ai</a> if
          this problem persists.
        </Alert>
      )}

      <div className={styles.gridSlot}>
        <ResourceView.Root>
          <DataTable<InvocationRecord>
            table={view.table}
            hasNextPage={Boolean(search.data?.next_token)}
            loading={loading}
            columns={columns}
            data={search.data?.items ?? []}
            noRowsContent={emptyRowsMessage(userFilters)}
          />
        </ResourceView.Root>
      </div>
    </div>
  );
};

/**
 * What an empty grid says.
 *
 * The page opens with a date filter already applied, so on a dataset that has never run an action
 * the reader's first sight of this list is an empty grid under a filter they did not set. Naming
 * the date range, and what to do about it, is the difference between that reading as "nothing here
 * yet" and as "something is hiding my rows". "No invocations yet" is reachable only once the
 * reader clears the chips, which is the one case where nothing is being hidden.
 */
function emptyRowsMessage(userFilters: Filter[]): string {
  if (userFilters.length === 0) {
    return "No invocations yet.";
  }
  if (userFilters.length === 1 && userFilters[0].field === CREATED) {
    return "No invocations in this date range. Widen the Created filter to see older ones.";
  }
  return "No invocations match these filters.";
}

/** Which of the places this list appears in: the panel remembers its expanded state per place. */
function activityContext({
  datasetId,
  action,
  triggerId,
  condition,
}: Pick<
  InvocationsProps,
  "datasetId" | "action" | "triggerId" | "condition"
>): string {
  if (condition) {
    return "custom";
  }
  if (datasetId) {
    return "dataset";
  }
  if (action) {
    return "action";
  }
  if (triggerId) {
    return "trigger";
  }
  return "org";
}

/** Auto-refresh sets the pace when on; otherwise the page polls only while a row is in flight. */
function pollInterval(autoRefresh: boolean, liveRows: boolean): number | false {
  if (autoRefresh) {
    return AUTO_REFRESH_INTERVAL_MS;
  }
  return liveRows ? LIVE_ROWS_POLL_MS : false;
}

function readStoredFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) === "true";
  } catch {
    return false;
  }
}

function writeStoredFlag(key: string, value: boolean): void {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    // Private mode or a full store: the choice just does not survive a reload.
  }
}
